import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import useCustomTracking from '../../../Util/useCustomTracking';
import UserBadge from './UserBadge';
import useProxy from '../../../hooks/useProxy';

interface UserSelectInfoProps {
  givenName: string;
  username: string;
  weeks: string[];
  selectedWeek: string;
}

const UserInfoSelect: React.FC<UserSelectInfoProps> = ({
  givenName,
  username,
  weeks,
  selectedWeek,
}: UserSelectInfoProps): React.ReactElement => {
  const [UserInfoSelectTrackingTag, userInfoSelectClick] = useCustomTracking(
    'dashboard-user-info-select',
    'dashboard-user-info-select-slot',
    {
      'data-csa-c-type': 'select',
    }
  );

  useEffect(() => userInfoSelectClick(), [selectedWeek]);

  // Simple comparison: if we're proxying and current username is different from stored proxy username
  const { isProxying, proxyUserName } = useProxy();
  const isViewingProxyProfile = isProxying && username === proxyUserName;

  return (
    <Box>
      <UserInfoSelectTrackingTag
        data-csa-c-selected-value={selectedWeek || weeks[0]}
      />
      <Row spacingInset='none none 500'>
        <Box>
          <Row>
            <Column>
              <Box width='62px' height='64px'>
                <UserBadge username={username} />
              </Box>
            </Column>
            <Column spacing='none' spacingInset='none none 100 none'>
              {isProxying ? (
                <>
                  {isViewingProxyProfile ? (
                    // Viewing proxy's own profile - show existing "Proxy as @alias" behavior
                    <Text type='h500'>
                      <FormattedMessage
                        id='cs_perfportal_proxy_as_header'
                        defaultMessage='Proxy as @{alias}'
                        values={{ alias: username }}
                        description='Proxy mode header'
                      />
                    </Text>
                  ) : (
                    // Viewing someone else's profile - show "You're viewing as" + normal user info
                    <>
                      <Text type='b200'>
                        <FormattedMessage
                          id='cs_perfportal_viewing_as_proxy_header'
                          defaultMessage="You're viewing as {proxyName}"
                          values={{ proxyName: proxyUserName }}
                          description='Proxy viewing indicator'
                        />
                      </Text>
                      <Text type='h500'>{givenName}</Text>
                      <Text type='b300'>{username}</Text>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Text type='h500'>{givenName}</Text>
                  <Text type='b300'>{username}</Text>
                </>
              )}
            </Column>
          </Row>
        </Box>
      </Row>
    </Box>
  );
};

export default UserInfoSelect;
